/**
 * Fetching and verifying the venue's configuration.
 *
 * Specification: ../test/config-client.spec.ts. Master plan §3 K8, §3 K4.
 *
 * The bundle carries the floor plan, the printer routing and the version
 * gates. It is cached on a device sitting on a restaurant LAN whose Wi-Fi
 * password is printed on the menu, so it is SIGNED — and the verifying key is
 * PINNED into the build rather than fetched alongside it, because a key that
 * arrives with the thing it authenticates authenticates nothing.
 *
 * A failed fetch is not an error the way a failed menu fetch is not an error:
 * the till keeps the bundle it has and carries on. That is the whole reason
 * the bundle exists rather than a settings endpoint.
 */

import { ConfigCache, type SignedConfigBundle } from "@gf/kernel/config";
import { importVerifyingKey } from "@gf/kernel/crypto";
import type { VenueClock } from "@gf/kernel/time";

export class ConfigFetchError extends Error {
  /** The HTTP status, when the server answered at all. 401: this device was unpaired. */
  readonly status: number | null;
  constructor(message: string, options?: { cause?: unknown; status?: number }) {
    super(message, options);
    this.name = "ConfigFetchError";
    this.status = options?.status ?? null;
  }
}

/** One table, as the floor plan lists it. */
export interface FloorTable {
  readonly tableRef: string;
  readonly name: string;
  readonly seats: number;
  readonly area: string;
}

/**
 * What a bundle from before modules existed means: what every venue had then.
 * A copy of the catalogue's core + DEFAULT_SELECTION, kept here so the till's
 * startup does not carry the whole catalogue; config-client.spec pins the two equal.
 */
export const LEGACY_MODULES: readonly string[] = ["sales", "tables", "kitchen-display", "accounting"];

export interface VenueConfig {
  readonly floor: readonly FloorTable[];
  /**
   * The modules in effect at the venue (`@gf/contracts/module/catalogue`).
   * A bundle from before modules existed means what every venue had then;
   * never an empty set, which would read as "no tables".
   */
  readonly modules: readonly string[];
  /** Every table's name, whether or not the floor is shown — a check opened on one stays named. */
  readonly tableNames: ReadonlyMap<string, string>;
  /** Counter tills and their letters, by device id; a device not here is at the tables. */
  readonly counters: Readonly<Record<string, string>>;
  /**
   * THE VENUE'S OWN CLOCK, so the till can name its business day offline.
   *
   * Every report the device produces is partitioned by one — a shift opened at
   * 23:50 and closed at 03:00 is ONE trading day — and `businessDateOf` needs a
   * timezone and a cutover the POS had no way to learn. Guessing from the
   * browser puts a device whose clock or locale is wrong on the wrong day,
   * silently, in a payload the server accepts.
   *
   * Typed as the kernel's own `VenueClock` so the two cannot drift into two
   * spellings of the same idea, and carried in the SIGNED bundle for the same
   * reason the floor plan is: a business day that shifts because somebody on
   * the LAN said so moves every total on the Z.
   */
  readonly clock: VenueClock;
  readonly printerRouting: Readonly<Record<string, string>>;
  /** Where *Tisk* sends a receipt — the office's choice. Null: the browser's own print dialog. */
  readonly receiptPrinter: { readonly printerId: string; readonly name: string } | null;
  readonly minOperatingVersion: number;
  /** From the regulatory registry. CZ rounds cash to whole koruna. */
  readonly cashIncrementMinor: bigint;
  /**
   * At or above this, a receipt must name the buyer — §30a ZDPH in CZ.
   *
   * From the same registry row as the increment above, and in the signed bundle
   * for the same reason the seller's IČO is: the till has to decide which kind
   * of document it is issuing while the network is down, and that decision is
   * the difference between a valid tax document and one a guest's employer
   * cannot deduct.
   */
  readonly simplifiedLimitMinor: bigint;
  /**
   * This venue's key for minting the code on the table.
   *
   * IN THE SIGNED BUNDLE so the till can mint one OFFLINE — §4 M11 requires an
   * "opaque POS-owned QR payload generated locally including offline", because
   * a code that needed the server could not be produced during the outage the
   * whole offline story exists for.
   *
   * Derived server-side from a platform secret and this venue id, so the till
   * never holds the platform secret and one venue's key says nothing about
   * another's. Null when the deployment has no guest surface, which is a state
   * the till shows rather than works around.
   */
  readonly guestTokenKey: string | null;
  /**
   * WHO IS SELLING, for the receipt.
   *
   * IN THE SIGNED BUNDLE, for the same reason the guest-token key is. A
   * receipt is a legal document and the DIČ on it is the venue's assertion
   * about who took the money; fetched unsigned it would be a value anybody on
   * the LAN could change. And it has to be ON THE DEVICE, because the outage
   * is exactly when the paper copy is the only one a guest gets.
   *
   * NULL IS A REAL STATE and not an error: a jurisdiction with no regime needs
   * none of this, and a venue that has not finished registering has none yet.
   * The till says what is missing rather than printing a document with a blank
   * where the IČO goes.
   */
  readonly seller: SellerIdentity | null;
  /**
   * The physical drawer this till rings into.
   *
   * NOT the device id, which is what it used to be: a Z-report named the
   * drawer by uuid, and two tills sharing one drawer were two drawers to every
   * report. In the SHARED_TILL model they are one, and which one is a fact
   * about the venue — so it comes from the signed bundle.
   *
   * Null is a real state: a deployment that has not named its drawers. The
   * till falls back to its device id, which is ugly and unambiguous.
   */
  readonly drawerId: string | null;
  /** A venue to practise on: every till of it says so, and nothing it sells is real. */
  readonly training: boolean;
}

export interface SellerIdentity {
  readonly legalName: string;
  /** IČO in CZ — the company number, not the tax one. */
  readonly registrationId: string;
  readonly vatId: string | null;
  readonly address: string | null;
}

/**
 * Read the payload into something typed, refusing anything malformed.
 *
 * A bundle that verified but carries nonsense is a bug on our side rather than
 * an attack, and it must still not reach the screen: a floor plan with a table
 * whose ref is missing renders a button that opens a check against nothing.
 */
/**
 * A whole number of minor units the bundle must carry.
 *
 * Refuses rather than substitutes: every caller of this is a jurisdictional
 * figure, and the value that would be substituted is always the Czech one.
 */
function requiredMinor(payload: Readonly<Record<string, unknown>>, field: string): bigint {
  const value = payload[field];
  if (typeof value !== "number" || !Number.isSafeInteger(value) || value < 0) {
    throw new ConfigFetchError(
      `the config bundle has no usable ${field} — it is a figure from the regulatory ` +
        `registry and the till must not invent one`,
    );
  }
  return BigInt(value);
}

export function readVenueConfig(payload: Readonly<Record<string, unknown>>): VenueConfig {
  const floor = payload["floor"];
  if (!Array.isArray(floor)) {
    throw new ConfigFetchError("the config bundle has no floor plan");
  }

  const tables: FloorTable[] = floor.map((raw, index) => {
    const t = raw as Record<string, unknown>;
    if (typeof t["tableRef"] !== "string" || t["tableRef"].length === 0) {
      throw new ConfigFetchError(`floor[${index}] has no tableRef`);
    }
    return {
      tableRef: t["tableRef"],
      name: typeof t["name"] === "string" ? t["name"] : t["tableRef"],
      seats: typeof t["seats"] === "number" ? t["seats"] : 0,
      area: typeof t["area"] === "string" ? t["area"] : "",
    };
  });

  const rawClock = payload["clock"];
  if (typeof rawClock !== "object" || rawClock === null) {
    // NOT DEFAULTED. Prague and 04:00 are right for every venue this product
    // has, which is exactly what makes a silent default dangerous: the first
    // venue it is wrong for is the one nobody thinks to check.
    throw new ConfigFetchError("the config bundle has no venue clock");
  }
  const c = rawClock as Record<string, unknown>;
  if (typeof c["timezone"] !== "string" || c["timezone"].length === 0) {
    throw new ConfigFetchError("the config bundle's clock has no timezone");
  }
  const startMinutes = c["businessDayStartMinutes"];
  if (
    typeof startMinutes !== "number" ||
    !Number.isInteger(startMinutes) ||
    startMinutes < 0 ||
    startMinutes >= 1_440
  ) {
    // Refused rather than clamped. 1500 minutes past midnight is not a time of
    // day, and clamping it to 1439 produces a business day rolling over at
    // 23:59 and a Z-report nobody can explain.
    throw new ConfigFetchError(
      `the config bundle's business-day cutover is not a time of day: ${String(startMinutes)}`,
    );
  }

  const rawModules = payload["modules"];
  const modules =
    Array.isArray(rawModules) && rawModules.length > 0 && rawModules.every((m) => typeof m === "string")
      ? (rawModules as string[])
      : [...LEGACY_MODULES];

  return {
    // Without the tables module the floor is empty; checks open on a table
    // still list, as tabs, so nothing is lost by switching it off mid-service.
    floor: modules.includes("tables") ? tables : [],
    modules,
    tableNames: new Map(tables.map((t) => [t.tableRef, t.name])),
    counters: (() => {
      const raw = payload["counters"];
      if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
      return Object.fromEntries(
        Object.entries(raw as Record<string, unknown>).filter(
          (e): e is [string, string] => typeof e[1] === "string" && /^[A-Z]$/.test(e[1]),
        ),
      );
    })(),
    clock: { timezone: c["timezone"], businessDayStartMinutes: startMinutes },
    printerRouting: (payload["printerRouting"] as Record<string, string>) ?? {},
    receiptPrinter: (() => {
      const p = payload["receiptPrinter"] as { printerId?: unknown; name?: unknown } | null | undefined;
      return p && typeof p.printerId === "string" && typeof p.name === "string" ? { printerId: p.printerId, name: p.name } : null;
    })(),
    minOperatingVersion:
      typeof payload["minOperatingVersion"] === "number" ? payload["minOperatingVersion"] : 1,
    /**
     * REFUSED RATHER THAN DEFAULTED, both of these.
     *
     * This field used to fall back to `100n` under a comment saying the
     * increment is DATA (D14) and "a second country is a registry row, not a
     * constant in the till". The fallback was that constant. A Slovak venue
     * whose bundle omitted the field would have rounded every cash sale to the
     * whole euro, silently, and the comment would still have read like a
     * guarantee.
     *
     * A bundle without them is a deployment that never published them, which is
     * a fixable mistake — and the same thing this function already does about a
     * missing floor plan.
     */
    cashIncrementMinor: requiredMinor(payload, "cashIncrementMinor"),
    simplifiedLimitMinor: requiredMinor(payload, "simplifiedLimitMinor"),
    /**
     * Both required fields or nothing.
     *
     * A half-filled seller renders a doklad with a blank where the IČO goes,
     * which is worse than no preview at all: it looks like a document and is
     * not one. `buildReceipt` would happily print `undefined`.
     */
    drawerId:
      typeof payload["drawerId"] === "string" && payload["drawerId"].trim()
        ? payload["drawerId"]
        : null,
    training: payload["training"] === true,
    seller: (() => {
      const raw = payload["seller"] as Record<string, unknown> | undefined;
      if (!raw) return null;
      const legalName = raw["legalName"];
      const registrationId = raw["registrationId"];
      if (typeof legalName !== "string" || typeof registrationId !== "string") return null;
      if (!legalName.trim() || !registrationId.trim()) return null;
      return {
        legalName,
        registrationId,
        vatId: typeof raw["vatId"] === "string" ? raw["vatId"] : null,
        address: typeof raw["address"] === "string" ? raw["address"] : null,
      };
    })(),
    // Absent is a real state: a deployment with no guest surface. The till
    // simply does not offer a code, rather than offering one nothing can open.
    guestTokenKey:
      typeof payload["guestTokenKey"] === "string" ? payload["guestTokenKey"] : null,
  };
}

export interface FetchConfigArgs {
  readonly baseUrl: string;
  readonly apiKey: string;
  readonly venueId: string;
  readonly fetch?: typeof fetch;
}

/** The signed bundle, unverified. The cache decides whether to trust it. */
export async function fetchConfigBundle(args: FetchConfigArgs): Promise<SignedConfigBundle> {
  const get = args.fetch ?? fetch;
  const url = `${args.baseUrl}/v1/config?venueId=${encodeURIComponent(args.venueId)}`;

  let response: Response;
  try {
    response = await get(url, { headers: { authorization: `Bearer ${args.apiKey}` } });
  } catch (error) {
    throw new ConfigFetchError("the config service is unreachable", { cause: error });
  }

  if (!response.ok) {
    throw new ConfigFetchError(`the config service answered ${response.status}`, { status: response.status });
  }

  try {
    return (await response.json()) as SignedConfigBundle;
  } catch (error) {
    throw new ConfigFetchError("the config service returned something that is not JSON", {
      cause: error,
    });
  }
}

/**
 * The cache, with the verifying key pinned at build time.
 *
 * PINNED rather than fetched. A device that downloaded the key alongside the
 * bundle would accept anything the network handed it, and the signature would
 * be decoration.
 */
export async function createConfigCache(args: {
  readonly publicKeyRaw: string;
  readonly tenantId: string;
  readonly venueId: string;
}): Promise<ConfigCache> {
  return ConfigCache.create(await importVerifyingKey(args.publicKeyRaw), {
    tenantId: args.tenantId,
    venueId: args.venueId,
  });
}
